import { useUser } from '@/features/account/services/User/hooks'
import { usePersona } from '@/features/business/hooks/usePersona'
import { GROUP_ID_PERSONAL } from '@/features/business/utils/selectedUserGroupCookie'
import { useEffect } from 'react'
import { getAnonymousId } from '@shipt/analytics-member-web'
import { getCookie, setCookie } from '@/features/shared/utils/cookies'
import { UNIVERSAL_ID } from '@shared/constants/segment'
import { datadogRum } from '@datadog/browser-rum'
import { useSession } from '@/features/authentication/utils/authentication/hooks/useSession'
import { getIsZipOnlySignedInUser } from '@/features/account/services/User/utils'
import { INFERRED_ZIP_SRC } from '@shared/constants/zip'
import { getIsConsentedToAnalytics } from '@/lib/transcend/utils'

export const useMonitorUser = () => {
  const user = useUser()
  const isZipOnlyUser = getIsZipOnlySignedInUser(user)
  const hasConsentedToAnalytics = getIsConsentedToAnalytics()
  const { isSessionValid, isSessionLoading } = useSession()
  const { activeGroup, isBusinessUser, isOwner } = usePersona()
  const { id, universal_id, has_purchased, has_placed_marketplace_order } = user ?? {}
  const subscriptionType = user?.exempt ? 'Exempt' : (user?.subscription?.type ?? 'None')
  const inferredZipSource = getCookie(INFERRED_ZIP_SRC)

  const activeGroupId = activeGroup?.id ?? GROUP_ID_PERSONAL

  useEffect(() => {
    if (isSessionLoading) return
    if (!isSessionValid) {
      datadogRum.setUser({
        id: getAnonymousId(),
        isLoggedIn: false,
      })
      return
    }
    if (!id || !universal_id) return

    const anonymousId = getAnonymousId()
    const userId = String(id)
    datadogRum.setUser({
      id: userId,
      anonymousId,
      universalId: universal_id,
      hasPlacedMarketplaceOrder: has_placed_marketplace_order,
      hasPurchased: has_purchased,
      subscriptionType,
      isLoggedIn: true,
      isZipOnlyUser,
      hasConsentedToAnalytics,
      activeGroupId,
      isBusinessUser,
      isGroupOwner: isOwner,
      ...(inferredZipSource && { inferredZipSource }),
    })
    // set universal_id cookie once per user
    setCookie(UNIVERSAL_ID, universal_id, { expires: 365 })
  }, [
    id,
    universal_id,
    has_placed_marketplace_order,
    subscriptionType,
    has_purchased,
    isSessionValid,
    isSessionLoading,
    isZipOnlyUser,
    inferredZipSource,
    hasConsentedToAnalytics,
    activeGroupId,
    isBusinessUser,
    isOwner,
  ])
}
